import {
  EInvoiceReportEmailAttribute,
  REPORT_EMAIL_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceReportEmailAttribute.js';
import {
  EInvoiceEncryptReportAttribute,
  ENCRYPTION_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceEncryptReportAttribute.js';
import {
  EInvoiceNotificationSnAttribute,
  NOTIFICATION_SN_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotificationSnAttribute.js';
import {
  EInvoiceNotificationEmailAttribute,
  NOTIFICATION_EMAIL_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotificationEmailAttribute.js';
import {
  EInvoiceNotifyMasterAttribute,
  NOTIFY_MASTER_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotifyMasterAttribute.js';
import {
  EInvoiceNotifySlavesAttribute,
  NOTIFY_SLAVES_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotifySlavesAttribute.js';
import { EInvoiceNotifyWithPrice } from './models/attributes/EInvoiceNotifyWithPrice.js';
import {
  EInvoicePasswordAttribute,
  PASSWORD_ATTRIBUTE_ID,
} from './models/attributes/EInvoicePasswordAttribute.js';
import {
  EInvoicePasswordConfirmAttribute,
  PASSWORD_CONFIRM_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceConfirmPasswordAttribute.js';
import {
  DAY_OF_WEEK_ATTRIBUTE_ID,
  EInvoiceNotificationTimeAttribute,
  OPTION_LIMITED,
  TIME_OF_DAY_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotificationTimeAttribute.js';
import {
  AmountAttribute,
  AMOUNT_ATTRIBUTE_ID,
  AMOUNT_ATTRIBUTE_TYPE,
} from './models/attributes/AmountAttribute.js';
import {
  SN_LIST_ATTRIBUTE_ID,
  SN_LIST_ATTRIBUTE_TYPE,
} from './models/attributes/SnListAttribute.js';
import {
  NEW_SN_LIST_ATTRIBUTE_ID,
  NEW_SN_LIST_ATTRIBUTE_TYPE,
} from './models/attributes/NewSnListAttribute.js';
import {
  GOOGLE_ACCOUNT_ATTRIBUTE_ID,
  GOOGLE_ACCOUNT_ATTRIBUTE_TYPE,
} from './models/attributes/GoogleAccountAttribute.js';
import {
  CONTACT_SN_ATTRIBUTE_ID,
  CONTACT_SN_ATTRIBUTE_TYPE,
} from './models/attributes/ContactSnAttribute.js';
import { EMAIL_ATTRIBUTE_ID, EMAIL_ATTRIBUTE_TYPE } from './models/attributes/EmailAttribute.js';
import { LICENCE_COUNT_ATTRIBUTE_TYPE } from './models/attributes/LicenceCountAttribute.js';
import {
  DATA_CAP_ATTRIBUTE_ID,
  DATA_CAP_ATTRIBUTE_TYPE,
} from './models/attributes/DataCapAttribute.js';
import { SN_ATTRIBUTE_ID, SN_ATTRIBUTE_TYPE } from './models/attributes/SnAttribute.js';
import {
  CONSUMED_AMOUNT_ATTRIBUTE_ID,
  CONSUMED_AMOUNT_ATTRIBUTE_TYPE,
} from './models/attributes/ConsumedAmount.js';
import {
  CYBER_FILTER_CONTACT_SN_ATTRIBUTE_ID,
  CYBER_FILTER_CONTACT_SN_ATTRIBUTE_TYPE,
} from './models/attributes/CyberFilterContactSn.js';
import { VOLUME_ATTRIBUTE_ID, VOLUME_ATTRIBUTE_TYPE } from './models/attributes/VolumeAttribute.js';
import {
  CyberFilterLicenseListAttribute,
  CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID,
  CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_TYPE,
} from './models/attributes/CyberFilterLicenseList.js';
import { SERVICE_UUIDS } from './constants.js';

export default class ReverseParser {
  /**
   * Returns attribute IDs based on ecare attribute values.
   * Some ecare attribute values represents different OST attribute IDs.
   * ReadOnly attributes are not mapped.
   *
   * @param {Service} service
   * @return {Map}
   * @static
   */
  static mapAttributeValuesToAttributeIds(service) {
    const { EINVOICE, CYBERFILTER } = SERVICE_UUIDS;
    const attributes = service.attributes.filter(attribute => attribute.isReadOnly === false);

    switch (service.uuid) {
      case EINVOICE:
        return ReverseParser._mapEInvoiceAttributes(attributes);
      case CYBERFILTER.SMART:
      case CYBERFILTER.PC:
        // CYBERFILTER.FAMILY is without attributes
        return ReverseParser._mapCyberFilterAttributes(attributes);
      default:
        return ReverseParser._mapGenericServiceAttributes(attributes);
    }
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {Map}
   */
  static _mapGenericServiceAttributes(attributes) {
    return new Map(
      attributes.map(attribute => {
        switch (attribute.type) {
          case AMOUNT_ATTRIBUTE_TYPE:
            return [AMOUNT_ATTRIBUTE_ID, attribute.value];
          case SN_LIST_ATTRIBUTE_TYPE:
            return [SN_LIST_ATTRIBUTE_ID, ReverseParser._parseSnListAttribute(attribute)];
          case NEW_SN_LIST_ATTRIBUTE_TYPE:
            return [NEW_SN_LIST_ATTRIBUTE_ID, ReverseParser._parseNewSnListAttribute(attribute)];
          case GOOGLE_ACCOUNT_ATTRIBUTE_TYPE:
            return [GOOGLE_ACCOUNT_ATTRIBUTE_ID, attribute.value];
          case CONTACT_SN_ATTRIBUTE_TYPE:
            return [CONTACT_SN_ATTRIBUTE_ID, attribute.value];
          case EMAIL_ATTRIBUTE_TYPE:
            return [EMAIL_ATTRIBUTE_ID, attribute.value];
          case LICENCE_COUNT_ATTRIBUTE_TYPE:
            return [AMOUNT_ATTRIBUTE_ID, attribute.value];
          case DATA_CAP_ATTRIBUTE_TYPE:
            return [DATA_CAP_ATTRIBUTE_ID, attribute.value];
          case CONSUMED_AMOUNT_ATTRIBUTE_TYPE:
            return [CONSUMED_AMOUNT_ATTRIBUTE_ID, attribute.value];
          case SN_ATTRIBUTE_TYPE:
            return [SN_ATTRIBUTE_ID, attribute.value];
          case CYBER_FILTER_CONTACT_SN_ATTRIBUTE_TYPE:
            return [CYBER_FILTER_CONTACT_SN_ATTRIBUTE_ID, attribute.value];
          case CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_TYPE:
            return [CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID, attribute.getApiValue()];
          case VOLUME_ATTRIBUTE_TYPE:
            return [VOLUME_ATTRIBUTE_ID, attribute.value];
          default:
            throw new Error(`Unhandled attribute ID: ${attribute.type}`);
        }
      }),
    );
  }

  /**
   *
   * @param {Attribute[]} eInvoiceAttributes
   * @return {Map}
   */
  static _mapEInvoiceAttributes(eInvoiceAttributes) {
    return new Map([
      [REPORT_EMAIL_ATTRIBUTE_ID, ReverseParser._parseEInvoiceReportEmail(eInvoiceAttributes)],
      [ENCRYPTION_ATTRIBUTE_ID, ReverseParser._parseEInvoiceEncryption(eInvoiceAttributes)],
      [PASSWORD_ATTRIBUTE_ID, ReverseParser._parseEInvoicePassword(eInvoiceAttributes)], // TODO might not be needed
      [
        PASSWORD_CONFIRM_ATTRIBUTE_ID,
        ReverseParser._parseEInvoicePasswordConfirm(eInvoiceAttributes),
      ], // TODO might not be needed
      [
        NOTIFICATION_SN_ATTRIBUTE_ID,
        ReverseParser._parseEInvoiceNotificationSn(eInvoiceAttributes),
      ],
      [
        NOTIFICATION_EMAIL_ATTRIBUTE_ID,
        ReverseParser._parseEInvoiceNotificationEmail(eInvoiceAttributes),
      ],
      [NOTIFY_MASTER_ATTRIBUTE_ID, ReverseParser._parseEInvoiceNotifyMaster(eInvoiceAttributes)],
      [NOTIFY_SLAVES_ATTRIBUTE_ID, ReverseParser._parseEInvoiceNotifySlaves(eInvoiceAttributes)],
      [DAY_OF_WEEK_ATTRIBUTE_ID, ReverseParser._parseEInvoiceDayOfWeek(eInvoiceAttributes)],
      [TIME_OF_DAY_ATTRIBUTE_ID, ReverseParser._parseEInvoiceTimeOfDay(eInvoiceAttributes)],
    ]);
  }

  /**
   * Special processing of cyberfilter services attributes.
   * License list is expanded/reduced to total number of licenses defined by `Amount` attribute value.
   *
   * @param {Attribute[]} eInvoiceAttributes
   * @return {Map}
   */
  static _mapCyberFilterAttributes(cyberFilterAttributes) {
    const amountAttribute = ReverseParser._getAttribute(cyberFilterAttributes, AmountAttribute);
    const cyberFilterLicenseList = ReverseParser._getAttribute(
      cyberFilterAttributes,
      CyberFilterLicenseListAttribute,
    );

    cyberFilterLicenseList.setNumberOfActiveLicenses(amountAttribute.value);

    const apiValue = cyberFilterLicenseList.licenses
      .map(license => [license.id, license.state, license.date, license.flag].join(';'))
      .join('#');

    return new Map([
      [AMOUNT_ATTRIBUTE_ID, amountAttribute.value],
      [CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID, apiValue],
    ]);
  }

  static _parseSnListAttribute(attribute) {
    // we strip all values exceeding maxValues parameters
    return `${attribute.values.slice(0, attribute.maxValues).join(';')};`;
  }

  static _parseNewSnListAttribute(attribute) {
    // we strip all values exceeding maxValues parameters and pass only the SNs without dates
    return `${attribute.values
      .slice(0, attribute.maxValues)
      .map(value => value.sn)
      .join(';')};`;
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceReportEmail(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoiceReportEmailAttribute);
  }

  /**
   * "Y" - pdf in zip with encryption
   * "P" - pdf without encryption
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceEncryption(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoiceEncryptReportAttribute) ? 'Y' : 'P';
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoicePassword(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoicePasswordAttribute);
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoicePasswordConfirm(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoicePasswordConfirmAttribute);
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceNotificationSn(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoiceNotificationSnAttribute);
  }

  /**
   *
   * @param {Attribute} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceNotificationEmail(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoiceNotificationEmailAttribute);
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceNotifyMaster(attributes) {
    const notifyMaster = ReverseParser._getAttributeValue(
      attributes,
      EInvoiceNotifyMasterAttribute,
    );
    const notifyWithPrice = ReverseParser._getAttributeValue(attributes, EInvoiceNotifyWithPrice);

    if (notifyMaster) {
      return notifyWithPrice ? 'Y' : 'Y_NOFIN';
    }
    return 'N';
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceNotifySlaves(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoiceNotifySlavesAttribute) ? 'Y' : 'N';
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceDayOfWeek(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoiceNotificationTimeAttribute) ===
      OPTION_LIMITED
      ? 'WORKDAYS'
      : 'UNLIMITED';
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @return {string}
   * @private
   */
  static _parseEInvoiceTimeOfDay(attributes) {
    return ReverseParser._getAttributeValue(attributes, EInvoiceNotificationTimeAttribute) ===
      OPTION_LIMITED
      ? 'DAY-TIME'
      : 'UNLIMITED';
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @param {function} classConstructor
   * @return {Attribute|undefined}
   * @private
   */
  static _getAttribute(attributes, classConstructor) {
    return attributes.find(attr => attr instanceof classConstructor);
  }

  /**
   *
   * @param {Attribute[]} attributes
   * @param {function} classConstructor
   * @return {*}
   * @private
   */
  static _getAttributeValue(attributes, classConstructor) {
    return ReverseParser._getAttribute(attributes, classConstructor).value;
  }
}
